import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import cx from "classnames";
import React, { useEffect, useRef, useState } from "react";

import styles from "./DatePicker.module.scss";

export interface DatePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onDateChange?: (startDate: Date | null, endDate: Date | null) => void;
  onApply?: (startDate: Date | null, endDate: Date | null) => void;
  onCancel?: () => void;
  isVisible?: boolean;
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  startDate = null,
  endDate = null,
  onDateChange,
  onApply,
  onCancel,
  isVisible = true,
  className = "",
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const holdIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handleDateClick = (day: number) => {
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    if (startDate && endDate) {
      // Reset selection and start new range
      onDateChange?.(selectedDate, null);
    } else if (startDate) {
      // Complete the range
      const newEndDate = new Date(
        Math.max(selectedDate.getTime(), startDate.getTime()),
      );
      const newStartDate = new Date(
        Math.min(selectedDate.getTime(), startDate.getTime()),
      );
      onDateChange?.(newStartDate, newEndDate);
    } else {
      // Start new range
      onDateChange?.(selectedDate, null);
    }
  };

  const handleApplyDates = () => {
    if (startDate && endDate) {
      onApply?.(startDate, endDate);
    }
  };

  const handleCancelDates = () => {
    onCancel?.();
  };

  const isDateInRange = (day: number): boolean => {
    if (!startDate || !endDate) return false;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return date >= startDate && date <= endDate;
  };

  const isDateSelected = (day: number): boolean => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return !!(
      (startDate && date.getTime() === startDate.getTime()) ||
      (endDate && date.getTime() === endDate.getTime())
    );
  };

  const isRangeStartDay = (day: number): boolean => {
    if (!startDate) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return date.getTime() === startDate.getTime();
  };

  const isRangeEndDay = (day: number): boolean => {
    if (!endDate) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return date.getTime() === endDate.getTime();
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleNavMouseDown = (direction: "prev" | "next") => {
    navigateMonth(direction);
    holdIntervalRef.current = setInterval(() => {
      navigateMonth(direction);
    }, 150);
  };

  const handleNavMouseUp = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);
      }
    };
  }, []);

  if (!isVisible) return null;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days: ({ day: number; isCurrentMonth: boolean } | null)[] = [];

  // Get previous month info for filler cells
  const prevMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() - 1,
    1,
  );
  const daysInPrevMonth = getDaysInMonth(prevMonth);

  // Add filler cells for days before the first day of the month (previous month)
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
    });
  }

  // Add days of the current month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({
      day,
      isCurrentMonth: true,
    });
  }

  // Add filler cells for remaining slots (next month)
  const totalCells = Math.ceil(days.length / 7) * 7;
  let nextMonthDay = 1;
  for (let i = days.length; i < totalCells; i++) {
    days.push({
      day: nextMonthDay,
      isCurrentMonth: false,
    });
    nextMonthDay++;
  }

  return (
    <div className={cx(styles.datePicker, className)}>
      <div className={styles.datePickerHeader}>
        <button
          type="button"
          onMouseDown={() => handleNavMouseDown("prev")}
          onMouseUp={handleNavMouseUp}
          onMouseLeave={handleNavMouseUp}
          className={styles.navButton}
        >
          <KeyboardArrowLeft />
        </button>
        <span className={styles.monthYear}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          type="button"
          onMouseDown={() => handleNavMouseDown("next")}
          onMouseUp={handleNavMouseUp}
          onMouseLeave={handleNavMouseUp}
          className={styles.navButton}
        >
          <KeyboardArrowRight />
        </button>
      </div>
      <div className={styles.weekDays}>
        {weekDays.map((day) => (
          <div key={day} className={styles.weekDay}>
            {day}
          </div>
        ))}
      </div>
      <div className={styles.daysGrid}>
        {days.map((dateInfo, index) => (
          <div key={index} className={styles.dayCell}>
            {dateInfo && (
              <button
                type="button"
                onClick={() =>
                  dateInfo.isCurrentMonth && handleDateClick(dateInfo.day)
                }
                className={cx(styles.dayButton, {
                  [styles.selectedDay]:
                    dateInfo.isCurrentMonth && isDateSelected(dateInfo.day),
                  [styles.rangeDay]:
                    dateInfo.isCurrentMonth &&
                    isDateInRange(dateInfo.day) &&
                    !isDateSelected(dateInfo.day),
                  [styles.rangeRowStart]:
                    dateInfo.isCurrentMonth &&
                    isDateInRange(dateInfo.day) &&
                    !isDateSelected(dateInfo.day) &&
                    index % 7 === 0,
                  [styles.rangeRowEnd]:
                    dateInfo.isCurrentMonth &&
                    isDateInRange(dateInfo.day) &&
                    !isDateSelected(dateInfo.day) &&
                    index % 7 === 6,
                  [styles.rangeStartDay]:
                    dateInfo.isCurrentMonth &&
                    isRangeStartDay(dateInfo.day) &&
                    startDate &&
                    endDate,
                  [styles.rangeEndDay]:
                    dateInfo.isCurrentMonth &&
                    isRangeEndDay(dateInfo.day) &&
                    startDate &&
                    endDate,
                  [styles.fillerDay]: !dateInfo.isCurrentMonth,
                })}
                disabled={!dateInfo.isCurrentMonth}
              >
                {dateInfo.day}
              </button>
            )}
          </div>
        ))}
      </div>
      <div className={styles.datePickerFooter}>
        <div className={styles.datePickerActions}>
          <button
            type="button"
            onClick={handleCancelDates}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApplyDates}
            className={cx(styles.applyButton, {
              [styles.applyButtonDisabled]: !startDate || !endDate,
            })}
            disabled={!startDate || !endDate}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
